import React, { useCallback, useMemo } from 'react';
import { FormattedMessage } from 'react-intl-next';
import type { LoomVideo } from '@loomhq/record-sdk';

import VideoIcon from '@atlaskit/icon/core/video';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { Box, Inline, Text, Flex, Pressable, xcss } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import Lozenge from '@atlaskit/lozenge';

import {
	LoomAdControl,
	useLoomRecorderEntryPoint,
	useLoomAdControlState,
	useLoomEntryPointVariant,
} from '@confluence/loom-utils';
import { useLoomInsertUrlActions } from '@confluence/loom-insert-url-store';
import { type FlagsStateContainer } from '@confluence/flags';
import { getUrlForContentType } from '@confluence/content-types-utils';
import { useSpaceKey } from '@confluence/space-utils';
import { useEditPageLoadingActions } from '@confluence/editor-loading-context';

import { messages } from '../messages';

import { ViewScriptButton } from './ViewScriptButton';

const marginBottom = xcss({
	marginBottom: 'space.100',
});
const pressableContainer = xcss({
	paddingTop: 'space.100',
	paddingBottom: 'space.100',
	background: token('color.background.neutral.subtle'),
	':hover': {
		background: token('color.background.neutral.subtle.hovered'),
	},
	':active': {
		background: token('color.background.neutral.subtle.pressed'),
	},
	width: '440px',
});

const pressableButton = xcss({
	background: token('color.background.neutral.subtle'),
	':hover': {
		background: token('color.background.neutral.subtle'),
	},
	':active': {
		background: token('color.background.neutral.subtle'),
	},
	display: 'flex',
	alignItems: 'center',
	padding: 'space.0',
});

const HorizontalDivider = xcss({
	borderBottom: `1px solid ${token('color.border')}`,
	height: '1px',
	width: '135px',
});

const contentType = 'page';

export const entryPointId = 'loom-confluence-read-time-dropdown-loom-script-extension';

export type LoadEditor = ({ args }: { [key: string]: string }) => void;

export type UseLoomScriptRecordingProps = {
	contentId: string;
	flags: FlagsStateContainer;
};

export const ReadTimeDropdownLoomScriptExtension = ({
	contentId,
	flags,
}: UseLoomScriptRecordingProps) => {
	const { loadEditor } = useEditPageLoadingActions();
	const { error, loading, entryPointVariant } = useLoomEntryPointVariant();
	const loomInsertUrlActions = useLoomInsertUrlActions();
	const spaceKey = useSpaceKey() || '';

	// readtime and rovo enable should pass from outside (nudge check it as well)
	const redirectUrl =
		getUrlForContentType({
			contentType,
			spaceKey,
			contentId,
			redirectToEditor: true,
		}) || '';

	const saveLoomUrlAndLoadEditor = useCallback(
		(video: LoomVideo) => {
			loomInsertUrlActions.updateFullPageEditorLoomUrl(video);

			loadEditor &&
				loadEditor({
					contentId,
					spaceKey,
					redirectUrl,
					contentType,
				});
		},
		[loomInsertUrlActions, contentId, spaceKey, loadEditor, redirectUrl],
	);

	const { isInterestedInLoom, notInterestedInLoomCallback } = useLoomAdControlState({
		touchpoint: entryPointId,
	});

	const handleNotInterested = useCallback(async () => {
		await notInterestedInLoomCallback();
	}, [notInterestedInLoomCallback]);

	const openLoomRecorderParams = useMemo(
		() => ({
			onInsert: (_: unknown, video: LoomVideo) => {
				saveLoomUrlAndLoadEditor(video);
			},
			// intentionally not localized because Loom RecordSDK does not support i18n
			insertButtonText: 'Insert Loom video',
		}),
		[saveLoomUrlAndLoadEditor],
	);

	const { openLoomRecorder } = useLoomRecorderEntryPoint({
		entryPointLocation: 'loomAIScriptReadMenuDropdown',
		flags,
		resetInsertFunctionOnUnmount: false,
	});

	const onRecordVideoClick = useCallback(() => {
		void openLoomRecorder(openLoomRecorderParams);
	}, [openLoomRecorder, openLoomRecorderParams]);

	if (!isInterestedInLoom || error) {
		return null;
	}

	return (
		<>
			<Box key="loomAIScriptSectionLabel" xcss={marginBottom}>
				<Inline alignBlock="center" spread="space-between">
					<Text color="color.text.subtlest" size="small">
						<FormattedMessage {...messages.readTimeImprovementLabel} />
					</Text>
					<Box xcss={HorizontalDivider} />
				</Inline>
			</Box>

			<Box key="createSummaryLoomVideo">
				<Pressable onClick={onRecordVideoClick} isDisabled={loading} xcss={pressableContainer}>
					<Flex justifyContent="space-between" alignItems="center">
						<Flex alignItems="center" gap="space.100">
							<VideoIcon size="small" label="Loom" />
							<FormattedMessage {...messages.readTimeLoomAIScriptTitle} />
							{entryPointVariant === 'CROSS_FLOW' && (
								<Lozenge appearance="new">
									<FormattedMessage {...messages.tryLozenge} />
								</Lozenge>
							)}
						</Flex>
						<Pressable
							xcss={pressableButton}
							onClick={(e) => {
								e.stopPropagation();
							}}
						>
							<ViewScriptButton />
							{entryPointVariant === 'CROSS_FLOW' && (
								<LoomAdControl
									entryPointId={entryPointId}
									onDismiss={handleNotInterested}
									spacing="default"
								/>
							)}
						</Pressable>
					</Flex>
				</Pressable>
			</Box>
		</>
	);
};
